/**
 * Web Storage access that never throws and never silently drops values.
 *
 * Some browsers block window storage (in-app webviews such as the Facebook/Instagram browsers,
 * Firefox ETP, Chrome with site data blocked). In those contexts simply touching localStorage or
 * sessionStorage throws. To keep working, every value is also held in an in-memory store for the
 * lifetime of the page. Reads prefer the real browser store and fall back to the in-memory copy,
 * so values written during this page session remain available even when the browser store is
 * unavailable — important for attribution and A/B assignment data that must not be lost.
 */

type StorageKind = "local" | "session";

const memory: Record<StorageKind, Map<string, string>> = {
  local: new Map(),
  session: new Map(),
};

function browserStorage(kind: StorageKind): Storage | null {
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function getItem(kind: StorageKind, key: string): string | null {
  const store = browserStorage(kind);
  if (store) {
    try {
      // Browser storage is working: trust it, including a genuine `null` for a missing key.
      return store.getItem(key);
    } catch {
      // Blocked at the method level: fall back to the in-memory copy.
    }
  }
  return memory[kind].get(key) ?? null;
}

function setItem(kind: StorageKind, key: string, value: string): void {
  const store = browserStorage(kind);
  if (store) {
    try {
      store.setItem(key, value);
      return;
    } catch {
      // Blocked: keep the value in memory so it is not lost this session.
    }
  }
  memory[kind].set(key, value);
}

function removeItem(kind: StorageKind, key: string): void {
  const store = browserStorage(kind);
  if (store) {
    try {
      store.removeItem(key);
      memory[kind].delete(key);
      return;
    } catch {
      // Blocked: fall through to clearing the in-memory copy.
    }
  }
  memory[kind].delete(key);
}

export interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const safeLocalStorage: SafeStorage = {
  getItem: key => getItem("local", key),
  setItem: (key, value) => setItem("local", key, value),
  removeItem: key => removeItem("local", key),
};

export const safeSessionStorage: SafeStorage = {
  getItem: key => getItem("session", key),
  setItem: (key, value) => setItem("session", key, value),
  removeItem: key => removeItem("session", key),
};
